/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pausedByUser = this.autoplay && this.motionPreference.matches;
      this.playing = false;
      this.ready = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.section = this.closest('.video-section') || this;
      if (this.background) this.createPlaybackControl();
      this.motionPreference.addEventListener('change', (event) => {
        if (event.matches && this.autoplay) {
          this.pausedByUser = true;
          this.pause();
        }
      });
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

    }

    createPlaybackControl() {
      this.section.classList.add('video-section--playback-control');
      this.playbackControl = document.createElement('button');
      this.playbackControl.type = 'button';
      this.playbackControl.className = 'night-video-playback';
      this.playbackControl.disabled = true;
      this.playbackControl.addEventListener('click', (event) => {
        event.stopPropagation();
        this.pausedByUser = this.playing;
        if (this.pausedByUser) this.pause();
        else this.play();
      });
      this.section.append(this.playbackControl);
      this.updatePlaybackControl();
    }

    updatePlaybackControl() {
      if (!this.playbackControl) return;
      this.playbackControl.disabled = !this.ready;
      this.playbackControl.textContent = this.playing ? theme.strings.pauseVideo : theme.strings.playVideo;
    }

    handlePlay() {
      this.playing = true;
      this.section.classList.add('video-section--played');
      this.updatePlaybackControl();
    }

    handlePause() {
      this.playing = false;
      if (this.inViewport) this.pausedByUser = true;
      this.updatePlaybackControl();
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this),
              onError: this.handlePause.bind(this),
              onAutoplayBlocked: this.handlePause.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this));
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: false,
          background: false,
          controls: !this.background,
          loop: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', this.handlePause.bind(this));
        this.player.on('play', this.handlePlay.bind(this));

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this));
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');
      if (!this.player) return;
      this.ready = true;
      if (this.autoplay && !this.background) this.player.controls = true;
      if (this.autoplay) this.player.autoplay = false;
      this.player.addEventListener('pause', this.handlePause.bind(this));
      this.player.addEventListener('play', this.handlePlay.bind(this));
      this.player.addEventListener('ended', this.handlePause.bind(this));
      const deferredMedia = this.closest('deferred-media');
      if (!deferredMedia || deferredMedia.loadTrigger !== 'click') this.player.muted = true;
      this.playing = !this.player.paused && !this.player.ended;
      if (this.pausedByUser) this.pause();
      else if (this.playing) this.handlePlay();
      this.updatePlaybackControl();
      this.addObserver(this.player);
      this.play();
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        this.handlePause();
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        this.playing = false;
        this.updatePlaybackControl();
        if (this.background) this.play();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.handlePlay();
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description || theme.strings.videoLabel;
      this.ready = true;
      this.updatePlaybackControl();
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     */
    play() {
      if (!this.player || !this.ready || this.pausedByUser) return;
      this.playing = true;
      this.updatePlaybackControl();

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        const playback = this.player.play();
        if (playback && playback.catch) playback.catch(() => {
          this.playing = false;
          this.updatePlaybackControl();
        });
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player || !this.ready) return;
      if (this.type === 'youtube') this.player.pauseVideo();
      else {
        const result = this.player.pause();
        if (result && result.catch) result.catch(() => {});
      }
      this.playing = false;
      this.updatePlaybackControl();
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
